import React from 'react';
import { Container, Row, Col, Card, Button, Badge } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import FAQSection from './FAQSection';
import './RetailEcommercePage.css';

const RetailEcommercePage = () => {
  const [heroRef, heroInView] = useInView({ threshold: 0.1 });
  const [featuresRef, featuresInView] = useInView({ threshold: 0.1 });
  const [benefitsRef, benefitsInView] = useInView({ threshold: 0.1 });
  const [processRef, processInView] = useInView({ threshold: 0.1 });

  const fadeInUp = {
    hidden: { opacity: 0, y: 50 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const slideInLeft = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  // const slideInRight = {
  //   hidden: { opacity: 0, x: 50 },
  //   visible: {
  //     opacity: 1,
  //     x: 0,
  //     transition: { duration: 0.8, ease: "easeOut" }
  //   }
  // };

  const keyFeatures = [
    {
      icon: "bi-basket",
      title: "Inventory & Order Management",
      description: "Real-time inventory tracking, automated reordering, and seamless order processing across all channels.",
      color: "#FF7A00"
    },
    {
      icon: "bi-credit-card",
      title: "Integrated Payment Solutions",
      description: "Secure payment processing with support for multiple payment methods and currencies.",
      color: "#0066CC"
    },
    {
      icon: "bi-graph-up",
      title: "Customer Relationship Management",
      description: "360-degree customer view with personalized marketing and customer service tools.",
      color: "#FF7A00"
    },
    {
      icon: "bi-truck",
      title: "Supply Chain & Distribution",
      description: "End-to-end supply chain visibility with optimized distribution and fulfillment.",
      color: "#0066CC"
    },
    {
      icon: "bi-bar-chart",
      title: "Analytics & Business Intelligence",
      description: "Advanced reporting and analytics for data-driven decision making and performance optimization.",
      color: "#FF7A00"
    },
    {
      icon: "bi-phone",
      title: "Omnichannel Experience",
      description: "Unified customer experience across web, mobile, in-store, and marketplace channels.",
      color: "#0066CC"
    }
  ];

  const benefits = [
    {
      icon: "bi-speedometer2",
      title: "Enhanced Customer Experience",
      description: "Deliver personalized shopping experiences with integrated customer data and preferences.",
      stats: "40% higher customer satisfaction"
    },
    {
      icon: "bi-arrow-up-circle",
      title: "Increased Sales Performance",
      description: "Boost revenue through optimized pricing, promotions, and cross-selling opportunities.",
      stats: "35% increase in sales"
    },
    {
      icon: "bi-clock-history",
      title: "Streamlined Operations",
      description: "Automate manual processes and reduce operational costs through integrated workflows.",
      stats: "50% faster processing"
    },
    {
      icon: "bi-shield-check",
      title: "Improved Inventory Control",
      description: "Reduce stockouts and overstock situations with intelligent demand forecasting.",
      stats: "30% reduction in excess inventory"
    },
    {
      icon: "bi-globe",
      title: "Multi-Channel Expansion",
      description: "Easily expand to new sales channels and marketplaces with unified management.",
      stats: "3x faster channel expansion"
    },
    {
      icon: "bi-graph-up-arrow",
      title: "Data-Driven Insights",
      description: "Make informed decisions with real-time analytics and comprehensive reporting.",
      stats: "25% improvement in margins"
    }
  ];

  const processSteps = [
    {
      title: "Unified Commerce Platform",
      description: "Single platform to manage all your retail and ecommerce operations",
      icon: "bi-layers"
    },
    {
      title: "Real-Time Visibility",
      description: "Complete visibility into inventory, orders, and customer data across all channels",
      icon: "bi-eye"
    },
    {
      title: "Scalable Growth",
      description: "Scale your business effortlessly as you expand to new markets and channels",
      icon: "bi-arrow-up-right-circle"
    },
    {
      title: "Enhanced Profitability",
      description: "Optimize pricing, reduce costs, and improve margins with intelligent automation",
      icon: "bi-currency-dollar"
    }
  ];

  return (
    <div className="retail-ecommerce-page">
      <Helmet>
        <title>NetSuite for Retail & eCommerce USA | SpireNest</title>
        <link rel="canonical" href="https://spirenest.co/netsuite/industries/retail-ecommerce" />
        <meta name="description" content="Boost your retail and eCommerce business with SpireNest's NetSuite services, enhancing customer experience and operational efficiency in the U.S. market." />
        <meta name="keywords" content="NetSuite retail, ecommerce ERP, retail management, online store solutions, USA, SpireNest" />
        <meta property="og:title" content="NetSuite for Retail & Ecommerce | SpireNest USA" />
        <meta property="og:description" content="Deliver seamless shopping experiences with NetSuite for retail and ecommerce businesses." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://spirenest.co/industries/retail-ecommerce" />
        <meta property="og:image" content="https://spirenest.co/assets/images/og-retail.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="NetSuite for Retail & Ecommerce | SpireNest USA" />
        <meta name="twitter:description" content="ERP solutions designed to power omnichannel retail and digital commerce." />
        <meta name="twitter:image" content="https://spirenest.co/assets/images/og-retail.jpg" />
      </Helmet>
      {/* Hero Section */}
      <section className="hero-section position-relative overflow-hidden">
        <div className="hero-bg-overlay"></div>
        <div className="floating-shapes">
          <motion.div className="shape-1" animate={{ rotate: 360, y: [-20, 20] }} transition={{ duration: 20, repeat: Infinity }} />
          <motion.div className="shape-2" animate={{ rotate: -360, x: [-30, 30] }} transition={{ duration: 25, repeat: Infinity }} />
          <motion.div className="shape-3" animate={{ y: [-15, 15], rotate: 180 }} transition={{ duration: 15, repeat: Infinity }} />
        </div>
        
        <Container className="position-relative">
          <Row className="align-items-center min-vh-100">
            <Col lg={7}>
              <motion.div
                ref={heroRef}
                initial="hidden"
                animate={heroInView ? "visible" : "hidden"}
                variants={staggerContainer}
                className="hero-content"
              >
                <motion.div variants={fadeInUp}>
                  <Badge className="hero-badge mb-4">
                    <i className="bi-shop me-2"></i>
                    RETAIL & ECOMMERCE
                  </Badge>
                </motion.div>
                
                <motion.h1 variants={slideInLeft} className="hero-title">
                  NetSuite ERP for <span style={{background: 'linear-gradient(135deg, #FF8F00, #FF6B35)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent'}}>Retail & Ecommerce</span>
                </motion.h1>
                
                <motion.p variants={slideInLeft} className="hero-description">
                  Transform your retail and ecommerce operations with our comprehensive NetSuite ERP solution. 
                  Unify your online and offline channels, streamline inventory management, and deliver exceptional 
                  customer experiences across all touchpoints. From order to fulfillment, manage your entire 
                  retail business with precision and agility.
                </motion.p>
              </motion.div>
            </Col>
            
            <Col lg={5}>
              <motion.div
                initial={{ opacity: 0, scale: 0.8, rotateY: 45 }}
                animate={{ opacity: 1, scale: 1, rotateY: 0 }}
                transition={{ duration: 1, delay: 0.5 }}
                className="hero-visual"
              >
                <div className="retail-visual">
                  <motion.div 
                    className="shopping-container"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                  >
                    <i className="bi-shop shopping-icon" style={{color: '#FF8F00'}}></i>
                  </motion.div>
                  <motion.div 
                    className="cart-small-1"
                    animate={{ rotate: -360 }}
                    transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
                  >
                    <i className="bi-cart-fill" style={{color: '#FF6B35'}}></i>
                  </motion.div>
                  <motion.div 
                    className="phone-small-2"
                    animate={{ y: [-10, 10] }}
                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <i className="bi-phone-fill" style={{color: '#FF8F00'}}></i>
                  </motion.div>
                </div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Key Features Section */}
      <section className="features-section py-5">
        <Container>
          <motion.div
            ref={featuresRef}
            initial="hidden"
            animate={featuresInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="section-header text-center mb-5">
              <h2 className="section-title">Key Features of NetSuite</h2>
              <p className="section-description">
                Comprehensive retail and ecommerce capabilities for modern businesses
              </p>
            </motion.div>
            
            <Row className="g-4">
              {keyFeatures.map((feature, index) => (
                <Col lg={4} md={6} key={index}>
                  <motion.div
                    variants={fadeInUp}
                    whileHover={{ y: -10, scale: 1.02 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <Card className="feature-card h-100 border-0">
                      <Card.Body className="p-4">
                        <motion.div 
                          className="feature-icon mb-3"
                          style={{ backgroundColor: `${feature.color}15`, color: feature.color }}
                          whileHover={{ rotate: 360, scale: 1.1 }}
                          transition={{ duration: 0.5 }}
                        >
                          <i className={`bi ${feature.icon}`}></i>
                        </motion.div>
                        <h5 className="feature-title mb-3">{feature.title}</h5>
                        <p className="feature-description">{feature.description}</p>
                        
                        <motion.div
                          className="feature-accent"
                          style={{ background: feature.color }}
                          initial={{ width: 0 }}
                          whileInView={{ width: "60px" }}
                          transition={{ duration: 0.8, delay: index * 0.1 }}
                        />
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section py-5">
        <Container>
          <motion.div
            ref={benefitsRef}
            initial="hidden"
            animate={benefitsInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <Row className="mb-5">
              <Col lg={12}>
                <motion.div variants={slideInLeft} className="text-center">
                  <h2 className="section-title">Who Benefits from NetSuite for Retail & Ecommerce</h2>
                  <p className="section-description">
                    Discover how retail and ecommerce businesses leverage NetSuite to accelerate growth, 
                    improve customer experience, and achieve operational excellence.
                  </p>
                </motion.div>
              </Col>
            </Row>
            
            <Row className="g-4">
              {benefits.map((benefit, index) => (
                <Col lg={4} md={6} key={index}>
                  <motion.div
                    variants={fadeInUp}
                    whileHover={{ scale: 1.05 }}
                    transition={{ type: "spring", stiffness: 300 }}
                    className="h-100"
                  >
                    <Card className="benefit-card h-100 border-0">
                      <Card.Body className="p-4 text-center">
                        <motion.div 
                          className="benefit-icon mb-3"
                          whileHover={{ rotate: [0, -10, 10, 0], scale: 1.1 }}
                        >
                          <i className={`bi ${benefit.icon}`}></i>
                        </motion.div>
                        <h5 className="benefit-title mb-3">{benefit.title}</h5>
                        <p className="benefit-description mb-3">{benefit.description}</p>
                        <Badge className="stats-badge">{benefit.stats}</Badge>
                      </Card.Body>
                      
                      <motion.div
                        className="card-accent"
                        initial={{ scaleX: 0 }}
                        whileInView={{ scaleX: 1 }}
                        transition={{ duration: 0.8, delay: index * 0.1 }}
                      />
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Process Benefits Section */}
      <section className="process-section py-5">
        <Container>
          <motion.div
            ref={processRef}
            initial="hidden"
            animate={processInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="section-header text-center mb-5">
              <h2 className="section-title">Transform Your Retail & Ecommerce Operations</h2>
            </motion.div>
            
            <Row className="g-4">
              {processSteps.map((step, index) => (
                <Col lg={3} md={6} key={index}>
                  <motion.div
                    variants={fadeInUp}
                    whileHover={{ y: -15 }}
                    className="process-card text-center"
                  >
                    <motion.div 
                      className="process-icon mb-4"
                      whileHover={{ scale: 1.2, rotate: 360 }}
                      transition={{ duration: 0.5 }}
                    >
                      <i className={`bi ${step.icon}`}></i>
                    </motion.div>
                    <h5 className="process-title mb-3">{step.title}</h5>
                    <p className="process-description">{step.description}</p>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="cta-section py-5">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <div className="cta-content">
              <motion.div
                className="cta-icon mb-4"
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <i className="bi-rocket-takeoff"></i>
              </motion.div>
              <h2 className="cta-title mb-4">Ready to Transform Your Retail Business?</h2>
              <p className="cta-description mb-4">
                Join thousands of retailers who have revolutionized their operations with NetSuite ERP
              </p>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link to="/contact" style={{ textDecoration: "none" }}>
                  <Button className="btn-primary-custom btn-lg px-5 py-3">
                    <i className="bi-chat-dots me-2"></i>
                    Get Started Today
                  </Button>
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </Container>
      </section>

      {/* FAQ Section */}
      <FAQSection />
    </div>
  );
};

export default RetailEcommercePage;